import styled from "@emotion/styled"
import { useLocale, useTranslations } from "next-intl"
import "swiper/css"
import "swiper/css/effect-coverflow"
import "swiper/css/effect-fade"
import "swiper/css/pagination"
import { NewText, useIsDesktop } from "ui"
import { Apartment } from "utils"
import ApartmentGallery from "../../ApartmentsForRent/ApartmentGallery"
import ApartmentPlan from "../../ApartmentsForRent/ApartmentPlan"
import ApartmentDocs from "./ApartmentDocs"
import ApartmentFeatures from "./ApartmentMarketingForRent/ApartmentFeatures"
import ApartmentPlanFRM from "./ApartmentMarketingForRent/ApartmentPlanFMR"
import BuildingFeatures from "./ApartmentMarketingForRent/BuildingFeatures"
import DrawerGallery from "./ApartmentMarketingForRent/DrawerGallery"
import { NewTabNameType, TabNameType } from "./ApartmentMarketingForRent/DrawerInner"
import ProjectLocation from "./ApartmentMarketingForRent/ProjectLocation"
import ApartmentSpec from "./ApartmentSpec"

const StyledPlan = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const StyledCompositeTab = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`
type DetailsProps = {
  apartment: Apartment
  type: TabNameType | NewTabNameType
  projectPage?: boolean
  imageGallery?: {
    apartmentThumbnail?: string
    apartmentGallery?: string[]
    numberOfRooms?: number
  }
  forDrawer?: boolean
  buildingCharacteristicsText?: string
}

function AccordionInnerDetails({
  apartment,
  type,
  projectPage,
  imageGallery,
  forDrawer,
  buildingCharacteristicsText,
}: DetailsProps) {
  const locale = useLocale()
  const isDesktop = useIsDesktop()
  const t = useTranslations("AccordionDetails")
  if (type === "plan") {
    return (
      <StyledPlan>
        {!forDrawer &&
          (isDesktop ? (
            <ApartmentGallery apartment={apartment} imageGallery={imageGallery} />
          ) : (
            <DrawerGallery
              images={
                imageGallery?.apartmentGallery?.length
                  ? imageGallery.apartmentGallery
                  : typeof apartment?.gallery?.[0] === "string"
                  ? apartment.gallery
                  : []
              }
              locale={locale}
              expandIcon
              mobileSlideHeight={222}
            />
          ))}
        <ApartmentSpec apartment={apartment} projectPage={projectPage} />
      </StyledPlan>
    )
  }
  if (type === "specs") {
    return <ApartmentPlan apartment={apartment} projectPage={projectPage} />
  }
  if (type === "docs") {
    return <ApartmentDocs apartment={apartment} />
  }
  if (type === "gallery") {
    return <ApartmentGallery apartment={apartment} />
  }
  if (type === "rentplan") {
    return <ApartmentPlan apartment={apartment} />
  }

  // for drawer
  if (type === "buildingFeatures") {
    return (
      <BuildingFeatures
        apartment={apartment}
        buildingCharacteristicsText={buildingCharacteristicsText}
      />
    )
  }
  if (type === "apartmentFeatures") {
    return <ApartmentFeatures apartment={apartment} />
  }
  if (type === "projectLocation") {
    return <ProjectLocation apartment={apartment} />
  }
  if (type === "apartmentPlan") {
    return <ApartmentPlanFRM apartment={apartment} />
  }
  if (type === "documents") {
    return <ApartmentDocs apartment={apartment} newStyle />
  }

  // Composite tabs for 2-tab layout (ASH-55)
  if (type === "apartmentInfo") {
    const hasDrawings = apartment.apartmentsDrawing && apartment.apartmentsDrawing.length > 0
    return (
      <StyledCompositeTab>
        <ApartmentFeatures apartment={apartment} />
        {hasDrawings && (
          <>
            <NewText
              component="p"
              desktopFontSize={20}
              desktopLineHeight={"1.2"}
              desktopFontWeight={700}
              mobileFontSize={18}
              mobileFontWeight={700}
              color="#000"
            >
              {t("apartmentPlan")}
            </NewText>
            <ApartmentPlanFRM apartment={apartment} />
          </>
        )}
        <ApartmentDocs apartment={apartment} newStyle />
      </StyledCompositeTab>
    )
  }
  if (type === "projectInfo") {
    return (
      <StyledCompositeTab>
        <BuildingFeatures
          apartment={apartment}
          buildingCharacteristicsText={buildingCharacteristicsText}
        />
        <ProjectLocation apartment={apartment} />
      </StyledCompositeTab>
    )
  }

  return null
}

export default AccordionInnerDetails
